import {Component, EventEmitter, Output, AfterViewChecked, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {BusyIndicatorService} from 'src/app/components/busy-indicator/busy-indicator.service';
import {LoadingDataService} from 'src/app/repositories/loading-data.service';
import {ValidationInfoService} from 'src/app/repositories/validation-info.service';
import {BusinessDataService} from '../../data-repositories/business-data.service';
import {AccountType} from '../../models/account-type';
import {User} from '../../models/user';
import {StagesNavigationService} from './stages-navigation/stages-navigation.service';
import {WorkspaceService} from './workspace.service';
import {DocumentsService} from '../../services/documents.service';

@Component({
  standalone: false,
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.scss'],
})
export class WorkspaceComponent implements AfterViewChecked, OnDestroy {

  @Output() localizeItem: EventEmitter<any> = new EventEmitter();
  private subscription = new Subscription();

  public userModel: User = new User();
  public isLoadingUserData: boolean;
  public isLoaded = false;

  videoTutorialSrc: string;
  public isNew: boolean;
  private isCreated = false;

  constructor(private service: BusinessDataService,
              private route: ActivatedRoute,
              private busyService: BusyIndicatorService,
              private translate: TranslateService,
              private documentsService: DocumentsService,
              public stagesNavigationService: StagesNavigationService,
              public toastr: ToastrService,
              public router: Router,
              public loadingService: LoadingDataService,
              public validationInfo: ValidationInfoService,
              workspaceService: WorkspaceService) {

    if (this.route.children.length < 1) {
      const stage = stagesNavigationService.workflowStages.find(x => x.canNavigate());
      this.router.navigate([{outlets: {stages: stage.stageOutlet}}], {
        relativeTo: this.route,
      });
    }

    this.subscription.add(this.service.GetUser().subscribe((s) => {
      this.userModel = s.model;
      this.isNew =
        !s.model.addressModel ||
        !s.model.addressModel.city ||
        s.model.addressModel.city === '';
      this.isLoaded = true;
    }));
    this.subscription.add(this.loadingService.getLoadingState().subscribe((state) => {
      this.isLoadingUserData = state;
    }));

    this.subscription.add(translate.onLangChange.subscribe(() => this.setVideoTutorialId()));
    this.subscription.add(router.events.subscribe(() => this.setVideoTutorialId()));

    this.subscription.add(workspaceService.stepChange.subscribe(step => {
      if (step > 0) {
        this.updateUser();
      } else if (step < 0) {
        this.back();
      }
    }));
  }

  ngAfterViewChecked(): void {
    this.setVideoTutorialId();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public updateUser(): void {
    this.validationInfo.resetAndValidate();

    if (!this.validationInfo.getValidateState()) {
      return;
    }

    const currentTab = this.stagesNavigationService.currentTab;

    if (currentTab && currentTab.requiresServerValidation) {
      this.busyService.beginProcess();
      this.service.validate(
        (x) => {
          this.busyService.endProcess();

          if (!x.isValid) {
            const route = this.stagesNavigationService.isMobile ? x.mobileRoute : x.route;
            this.router.navigate(
              [{outlets: {stages: this.resolveValidateRoute(route)}}],
              {relativeTo: this.route, queryParams: {validate: true}}
            );
            this.toastr.warning(this.translate.instant(x.errorKey));
            return;
          }

          this.advanceToNextStage();
        },
        () => this.busyService.endProcess()
      );
      return;
    }

    this.advanceToNextStage();
  }

  private advanceToNextStage(): void {
    const nextStage = this.stagesNavigationService.next();

    if (nextStage) {
      if (!!nextStage.applicationStatus) {
        this.userModel.applicationStatus = nextStage.applicationStatus;
      }

      this.service.updateUserImmediatly(this.userModel, () => {
      });

      this.router.navigate([{outlets: {stages: nextStage.stageOutlet}}], {
        relativeTo: this.route
      }).then(() => this.scrollToTop());
      return;
    }

    this.finalizeApplication();
  }

  private resolveValidateRoute(route: string): string {
    if (!route || this.userModel?.accountType !== AccountType.Joint) {
      return route;
    }

    const jointMap: Record<string, string> = {
      personal: 'personal-first-holder',
      address: 'address-first-holder',
      employment: 'employment-first-holder',
      substitute: 'substitute-first-holder',
      documents: 'documents-first-holder',
    };

    return jointMap[route] || route;
  }

  private finalizeApplication(): void {
    if (this.isCreated) {
      return;
    }

    this.busyService.beginProcess();

    const handleFinalizeError = () => {
      this.busyService.endProcess();
      this.isCreated = false;
      this.toastr.error(this.translate.instant('Common.SubmitFailed'));
    };

    this.service.updateUserImmediatly(
      this.userModel,
      () => {
        this.isCreated = true;
        this.service.finishApplication(
          () => {
            this.busyService.endProcess();

            const isValidDocuments = this.documentsService.isValidDocuments(this.userModel);

            if (isValidDocuments) {
              this.router.navigate(['thank-you']);
            } else {
              this.router.navigate(['thank-you-without-documents']);
            }
          },
          handleFinalizeError
        );
      },
      handleFinalizeError
    );
  }

  back() {
    // this.validationInfo.resetAndValidate();

    // if (this.validationInfo.getValidateState()) {

    const previousStage = this.stagesNavigationService.previous();

    this.userModel.applicationStatus = previousStage.applicationStatus;
    this.router.navigate([{outlets: {stages: previousStage.stageOutlet}}], {relativeTo: this.route})
      .then(() => this.scrollToTop());
    this.service.updateUserImmediatly(this.userModel, () => {
    });
    // }
  }

  private scrollToTop() {
    window.scrollTo(0, 0);
    const scroller = document.querySelector('app-workspace .scroll-content');
    if (scroller) {
      scroller.scrollTop = 0;
    }
  }

  private setVideoTutorialId() {
    const tutorialKey = this.getTutorialKey();
    const value = tutorialKey ? this.translate.instant(tutorialKey) : null;
    this.videoTutorialSrc = value === tutorialKey ? null : value;
  }

  private getTutorialKey(): string {
    if (!this.stagesNavigationService.currentTab) {
      return null;
    }

    const base = 'VideoTutorial';
    const device = this.stagesNavigationService.isMobile ? 'Mobile' : '';

    switch (this.stagesNavigationService.currentTab.stageOutlet) {
      case 'purpose-type':
        return `${base}${device}.PurposeType`;
      case 'account-type':
        return `${base}${device}.AccountType`;
      case 'personal':
        return `${base}${device}.PersonalSingle`;
      case 'address':
        return `${base}${device}.AddressSingle`;
      case 'employment':
        return `${base}${device}.EmploymentSingle`;
      case 'personal-first-holder':
        return `${base}${device}.PersonalFirstHolder`;
      case 'address-first-holder':
        return `${base}${device}.AddressFirstHolder`;
      case 'employment-first-holder':
        return `${base}${device}.EmploymentFirstHolder`;
      case 'personal-second-holder':
        return `${base}${device}.PersonalSecondHolder`;
      case 'address-second-holder':
        return `${base}${device}.AddressSecondHolder`;
      case 'employment-second-holder':
        return `${base}${device}.EmploymentSecondHolder`;
      case 'experience':
        return `${base}${device}.Experience`;
      case 'source-of-wealth':
        return `${base}${device}.SourceOfWealth`;
      case 'regulatory':
        return `${base}${device}.Regulatory`;
      case 'investment':
        return `${base}${device}.Investment`;
      case 'substitute':
        return `${base}${device}.SubstituteSingle`;
      case 'substitute-first-holder':
        return `${base}${device}.SubstituteFirstHolder`;
      case 'substitute-second-holder':
        return `${base}${device}.SubstituteSecondHolder`;
      case 'general-agreement':
        return `${base}${device}.GeneralAgreement`;
      case 'documents':
        return `${base}${device}.DocumentsSingle`;
      case 'documents-first-holder':
        return `${base}${device}.DocumentsFirstHolder`;
      case 'documents-second-holder':
        return `${base}${device}.DocumentsSecondHolder`;
      default:
        return null;
    }
  }
}
